import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import BomToolbar from "./BomToolbar";
import { BomEmptyState } from "./BomTable";
import { downloadCsv } from "./bomExport";
import CatalogStatusChip, {
    catalogStatusLabel,
    normalizeCatalogStatus,
} from "../productCatalog/CatalogStatusChip";
import SupplySku from "../SupplySku";
import {
    clearBomFilters,
    clearBomRowSelection,
    selectBomUi,
    setBomExpandedRows,
    setBomFilter,
    toggleBomExpandedRow,
} from "../../slices/bomUiSlice";

function StatusBadge({ status }) {
    const normalizedStatus = normalizeCatalogStatus(status);
    return normalizedStatus ? (
        <CatalogStatusChip status={normalizedStatus} label={catalogStatusLabel(normalizedStatus)} />
    ) : (
        "—"
    );
}

function formatMoney(value) {
    return `$${Number(value || 0).toFixed(2)}`;
}

function BundleAccordionRow({ row, isExpanded, onToggle }) {
    return (
        <>
            <tr className="border-b border-gray-100 bg-white hover:bg-gray-50/70" onClick={onToggle}>
                <td className="px-2 py-3">
                    {row.components.length ? (
                        <button
                            type="button"
                            className="rounded p-1 text-gray-400 transition"
                        >
                            {isExpanded ? (
                                <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                                <ChevronRightIcon className="h-4 w-4" />
                            )}
                        </button>
                    ) : null}
                </td>
                <td className="px-4 py-3">
                    <SupplySku sku={row.bundleSku} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{row.bundleName || "—"}</td>
                <td className="px-4 py-3 text-right text-sm text-gray-400">{row.componentCount} SKUs</td>
                <td className="px-4 py-3 text-right text-sm text-gray-700">{formatMoney(row.totalBomCost)}</td>
                <td className="px-4 py-3 text-sm text-gray-700">
                    <StatusBadge status={row.status} />
                </td>
            </tr>
            {isExpanded && row.components.length ? (
                <tr className="border-b border-gray-100 bg-gray-50/70">
                    <td colSpan={6}>
                        <div className="px-6 py-3 pl-14">
                            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                                <table className="min-w-full border-collapse text-left text-sm">
                                    <thead className="border-b border-gray-200 bg-gray-100">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                                Component SKU
                                            </th>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                                Supplier
                                            </th>
                                            <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                                Qty
                                            </th>
                                            <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                                Unit Cost
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {row.components.map((component, index) => (
                                            <tr key={component.id || `${row.id}-${index}`}>
                                                <td className="px-3 py-2">
                                                    <SupplySku sku={component.componentSku} />
                                                </td>
                                                <td className="px-3 py-2 text-gray-500">{component.supplier || "—"}</td>
                                                <td className="px-3 py-2 text-right">{component.qty}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">
                                                    {formatMoney(component.unitCost)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </td>
                </tr>
            ) : null}
        </>
    );
}

const BundleBomPanel = ({ onMetricsChange }) => {
    const dispatch = useDispatch();
    const { filtersByTab, expandedIdsByTab, data } = useSelector(selectBomUi);
    const filters = filtersByTab.bundle;
    const expandedIds = expandedIdsByTab.bundle;
    const bundleRows = data.bundles || [];

    useEffect(() => {
        dispatch(clearBomRowSelection("bundle"));
    }, [dispatch]);

    const bundleGroupOptions = useMemo(() => {
        const byId = new Map();
        for (const row of bundleRows) {
            const gid = row.groupId != null && String(row.groupId).trim() !== "" ? String(row.groupId).trim() : "";
            if (!gid) continue;
            const label = String(row.bundleName || "").trim();
            if (!label || label === "—" || label === "-") continue;
            if (!byId.has(gid)) {
                byId.set(gid, { value: gid, label });
            }
        }
        return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [bundleRows]);

    const filteredRows = useMemo(() => {
        const search = String(filters.search || "").trim().toLowerCase();
        const groupFilter = filters.productGroupId ?? "all";
        return bundleRows.filter((item) => {
            const matchesGroup =
                groupFilter === "all" ||
                groupFilter === "" ||
                (item.groupId != null && String(item.groupId) === String(groupFilter));
            const matchesSearch =
                !search ||
                [
                    item.bundleSku,
                    item.bundleName,
                    item.status,
                    ...item.components.map((component) => component.componentSku),
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase()
                    .includes(search);
            return matchesGroup && matchesSearch;
        });
    }, [bundleRows, filters]);

    const allExpanded =
        filteredRows.length > 0 && filteredRows.every((row) => expandedIds.includes(row.id));

    useEffect(() => {
        onMetricsChange?.({
            totalRows: filteredRows.length,
            selectedRows: 0,
            totalCost: filteredRows.reduce((sum, row) => sum + Number(row.totalBomCost || 0), 0),
        });
    }, [filteredRows, onMetricsChange]);

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col">
            <BomToolbar
                filters={filters}
                onFilterChange={(key, value) => dispatch(setBomFilter({ tab: "bundle", key, value }))}
                onClearFilters={() => dispatch(clearBomFilters("bundle"))}
                secondaryOptions={bundleGroupOptions}
                secondaryPlaceholder="All Products"
                secondaryFilterKey="productGroupId"
                hideAddButton
                showExport={false}
                searchPlaceholder="Search bundle SKU, status, or component SKU..."
                onExport={() =>
                    downloadCsv("bom-bundles.csv", [
                        ["Bundle SKU", "Bundle Name", "Components", "Total BOM Cost", "Status"],
                        ...filteredRows.map((row) => [
                            row.bundleSku,
                            row.bundleName,
                            row.componentCount,
                            row.totalBomCost,
                            row.status,
                        ]),
                    ])
                }
            />
            <div className="min-h-0 flex-1">
                {filteredRows.length === 0 ? (
                    <BomEmptyState
                        title="No bundle BOM rows found"
                        description="Bundle BOMs appear here for products that are virtual and have kit components in ShipHero."
                    />
                ) : (
                    <div className="overflow-hidden border border-gray-200 bg-white shadow-sm">
                        <div className="overflow-auto">
                            <table className="min-w-full border-collapse text-left">
                                <thead className="bg-gray-50">
                                    <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
                                        <th className="w-10 px-2 py-3" />
                                        <th className="px-4 py-3 font-semibold">Bundle SKU</th>
                                        <th className="px-4 py-3 font-semibold">Bundle Name</th>
                                        <th className="px-4 py-3 text-right font-semibold">Components</th>
                                        <th className="px-4 py-3 text-right font-semibold">Total BOM Cost</th>
                                        <th className="px-4 py-3 font-semibold">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map((row) => (
                                        <BundleAccordionRow
                                            key={row.id}
                                            row={row}
                                            isExpanded={expandedIds.includes(row.id)}
                                            onToggle={() => dispatch(toggleBomExpandedRow({ tab: "bundle", id: row.id }))}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BundleBomPanel;
